import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import axios from "axios";
import categoryFieldsConfig from "./categoryFieldsConfig2";
import ImagePopup from "../../../../../utils/imagePopup";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ApproveVendorServiceForm = ({ open, onClose, vendorData, onSubmit }) => {
  const [status, setStatus] = useState(vendorData.status); // Default to the current status of the vendor
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedBadges, setSelectedBadges] = useState(
    vendorData.SpecialBadges || []
  );

  const availableBadges = [
    "Available for Video Meetings",
    "Most Popular",
    "Premium",
    "Prime",
    "None",
  ];

  // Handle badge selection
  const handleBadgeChange = (badge) => {
    setSelectedBadges((prev) =>
      prev.includes(badge)
        ? prev.filter((item) => item !== badge)
        : [...prev, badge]
    );
  };

  // Handle Approve button click
  const handleApprove = async () => {
    setIsLoading(true);

    const updatedPayload = {
      ...vendorData,
      status: "approved",
      SpecialBadges: JSON.stringify(selectedBadges),
    };

    try {
      const response = await axios.put(
        `${BASE_URL}/api/v1/VendorManagement/show-Service/${vendorData.id}/status`,
        updatedPayload
      );

      console.log("Vendor approved with badges:", response.data);
      onSubmit(updatedPayload);
      onClose();
    } catch (error) {
      console.error("Error approving vendor:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpecialApprove = async () => {
    setIsLoading(true);

    const updatedPayload = {
      ...vendorData,
      SpecialBadges: JSON.stringify(selectedBadges),
    };

    try {
      // API call to update the SpecialBadges
      const response = await axios.put(
        `${BASE_URL}/api/v1/VendorManagement/show-Service/${vendorData.id}/SpecialBadges`,
        updatedPayload
      );

      // Log success
      console.log("Vendor badges updated successfully:", response.data);

      // Optimistic UI update
      onSubmit({
        ...vendorData,
        SpecialBadges: JSON.stringify(selectedBadges),
      });

      // Close the dialog
      onClose();
    } catch (error) {
      console.error("Error updating special badges:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    setIsLoading(true);

    try {
      const formData = new FormData();

      // Define common fields to append
      const commonFields = [
        "id",
        "Vendor_ID",
        "ServiceCategory_ID",
        "ServiceName",
        "ServiceDescription",
        "Location",
        "City",
        "CompanyName",
      ];

      // Append common fields
      commonFields.forEach((field) => {
        if (vendorData[field]) {
          formData.append(field, vendorData[field]);
        }
      });

      // Append ServiceLocations as JSON
      if (vendorData.ServiceLocations) {
        formData.append(
          "ServiceLocations",
          JSON.stringify(vendorData.ServiceLocations)
        );
      }

      // Append SocialMediaHandle as JSON
      if (vendorData.SocialMediaHandle) {
        formData.append(
          "SocialMediaHandle",
          JSON.stringify(vendorData.SocialMediaHandle)
        );
      }

      // Append BannerImage (single or multiple images)
      if (Array.isArray(vendorData.BannerImage)) {
        vendorData.BannerImage.forEach((image) => {
          formData.append("BannerImage", image);
        });
      } else if (vendorData.BannerImage) {
        formData.append("BannerImage", vendorData.BannerImage);
      }

      // Append Images (gallery images)
      if (Array.isArray(vendorData.Images)) {
        vendorData.Images.forEach((img) => {
          formData.append("Images", img);
        });
      }

      // Append Video (single or multiple videos)
      if (Array.isArray(vendorData.Video)) {
        vendorData.Video.forEach((vid) => {
          formData.append("Video", vid);
        });
      } else if (vendorData.Video) {
        formData.append("Video", vendorData.Video);
      }

      // Append TotalBudget if available
      if (vendorData.TotalBudget) {
        formData.append("TotalBudget", vendorData.TotalBudget);
      }

      // Append SpecialBadges as JSON
      if (selectedBadges && selectedBadges.length > 0) {
        formData.append("SpecialBadges", JSON.stringify(selectedBadges));
      }

      // Handle DynamicData (other fields)
      const dynamicData = {};
      Object.entries(vendorData.DynamicData || {}).forEach(([key, value]) => {
        dynamicData[key] = value;
      });
      formData.append("DynamicData", JSON.stringify(dynamicData));

      // Append status
      formData.append("status", "approved");

      // API call
      const response = await axios.post(
        `${BASE_URL}/api/v1/VendorManagement/Update-Vendor-Service`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Vendor approved with badges:", response.data);
      onSubmit(vendorData);
      onClose();
    } catch (error) {
      console.error("Error approving vendor:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPending = async () => {
    setIsLoading(true);
    const updatedVendor = { ...vendorData, status: "pending" };

    try {
      // Make API request to update the vendor status to "pending"
      const response = await axios.put(
        `${BASE_URL}/api/v1/VendorManagement/show-Service/${updatedVendor.id}/status`,
        { status: "pending" }
      );

      console.log("Vendor status set to pending:", response.data);
      onSubmit(updatedVendor); // Notify parent about the status change
      onClose(); // Close the dialog
    } catch (error) {
      console.error("Error setting vendor to pending:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    setIsLoading(true);
    const updatedVendor = { ...vendorData, status: "rejected" };

    try {
      // Make API request to update the vendor status to "approved"
      const response = await axios.put(
        `${BASE_URL}/api/v1/VendorManagement/show-Service/${updatedVendor.id}/status`, // Pass the vendor id in the URL
        { status: "rejected" } // Send status in the request body
      );

      console.log("Vendor approved:", response.data);
      onSubmit(updatedVendor); // Notify parent component to update the status
      onClose(); // Close the dialog
    } catch (error) {
      console.error("Error approving vendor:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderCategoryFields = () => {
    if (!vendorData) {
      return <p>No vendor data available.</p>;
    }

    const formatDynamicData = (data) => {
      if (typeof data === "string") {
        try {
          data = JSON.parse(data); // Parse if it's a stringified JSON
        } catch (error) {
          console.error("Error parsing DynamicData:", error);
          return "Invalid Data Format";
        }
      }

      if (typeof data === "object" && data !== null) {
        // Generate key-value pairs from the object
        return Object.entries(data).map(([key, value]) => {
          // Format array values as comma-separated strings
          if (Array.isArray(value)) {
            return (
              <div key={key}>
                <strong>{key}:</strong> {value.map((v) => v.trim()).join(", ")}
              </div>
            );
          }
          // Format non-array values directly
          return (
            <div key={key}>
              <strong>{key}:</strong> {value}
            </div>
          );
        });
      }

      return "N/A"; // Fallback for unsupported formats
    };

    const fieldsToDisplay = [
      { label: "Service Name", value: vendorData.ServiceName },
      { label: "City", value: vendorData.City },
      { label: "Total Budget", value: vendorData.TotalBudget },
      {
        label: "Service provided by the vendor",
        value: formatDynamicData(vendorData.DynamicData),
      },
    ];

    return fieldsToDisplay.map((field, index) => (
      <div key={index} className="field-name">
        <label>{field.label}:</label>
        <div className="field-value">
          {/* Check if the field's value is JSX (array of elements) */}
          {Array.isArray(field.value) ? field.value : field.value || "N/A"}
        </div>
      </div>
    ));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Vendor Details</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="h6">Service Name:</Typography>
            <Typography>{vendorData.ServiceName}</Typography>
          </Grid>

          {/* <Grid item xs={6}>
              <Typography variant="h6">Category:</Typography>
              <Typography>{vendorData.Category?.CategorieName}</Typography>
            </Grid> */}

          <Grid item xs={6}>
            <Typography variant="h6">Service Description:</Typography>
            <Typography>{vendorData.ServiceDescription}</Typography>
          </Grid>

          {/* <Grid item xs={6}>
              <Typography variant="h6">Service Required:</Typography>
              <Typography>
                {Array.isArray(vendorData.ServiceSpecialBadge)
                  ? vendorData.ServiceSpecialBadge.join(", ")
                  : "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="h6">Special Badge:</Typography>
              <Typography>
                {Array.isArray(vendorData.ServiceRequired)
                  ? vendorData.ServiceRequired.join(", ")
                  : "N/A"}
              </Typography>
            </Grid> */}

          <Grid item xs={6}>
            <Typography variant="h6">Vendor Name:</Typography>
            <Typography>{`${vendorData.Vendor.firstName} ${vendorData.Vendor.lastName}`}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h6">Service Locations:</Typography>
            <Typography>{`${JSON.parse(
              vendorData.ServiceLocations
            )} `}</Typography>
          </Grid>

          <Grid item xs={12}>
            {renderCategoryFields()}
          </Grid>

          {/* Render category fields for special badges */}
          <Grid item xs={12}>
            <Typography variant="h6">Special Badges:</Typography>
            <div>
              {vendorData.status === "approved" ? (
                availableBadges.map((badge) => (
                  <FormControlLabel
                    key={badge}
                    control={
                      <Checkbox
                        checked={selectedBadges.includes(badge)}
                        onChange={() => handleBadgeChange(badge)}
                      />
                    }
                    label={badge}
                  />
                ))
              ) : (
                <Typography>
                  {JSON.parse(vendorData.SpecialBadges).join(", ")}
                </Typography>
              )}
            </div>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h6">Email:</Typography>
            <Typography>{vendorData.Vendor.email}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h6">Mobile Number:</Typography>
            <Typography>{vendorData.Vendor.mobile_number}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h6">City:</Typography>
            <Typography>{vendorData.City}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h6">Address:</Typography>
            <Typography>{vendorData.Location}</Typography>
          </Grid>
          {/* 
            <Grid item xs={6}>
              <Typography variant="h6">Service Available Day:</Typography>
              <Typography>{vendorData.ServiceAvailableDay}</Typography>
            </Grid> */}
          {/* <Grid item xs={6}>
            <Typography>
              {Array.isArray(vendorData.Booking_Dates) &&
              vendorData.Booking_Dates.length > 0
                ? vendorData.Booking_Dates.join(", ")
                : "N/A"}
            </Typography>
          </Grid> */}

          {/* <Grid item xs={6}>
              <Typography variant="h6">Rating:</Typography>
              <Typography>{vendorData.Rating}</Typography>
            </Grid> */}

          <Grid item xs={6}>
            <Typography variant="h6">Status:</Typography>
            <Typography
              sx={{
                color:
                  status === "approved"
                    ? "green"
                    : status === "pending"
                    ? "orange"
                    : "red",
              }}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h6">Created At:</Typography>
            <Typography>
              {new Date(vendorData.createdAt).toLocaleString()}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h6">Updated At:</Typography>
            <Typography>
              {new Date(vendorData.updatedAt).toLocaleString()}
            </Typography>
          </Grid>

          {vendorData.Image && (
            <Grid item xs={12}>
              <Typography variant="h6">Venue Images:</Typography>
              <Grid container spacing={2}>
                {JSON.parse(vendorData.Image).map((img, index) => (
                  <Grid item xs={3} key={index}>
                    <img
                      src={
                        `${BASE_URL}${img.path
                          .replace("/home/ubuntu/new/CEP_BackEnd", "")
                          .replace("/home/CEP_BackEnd", "")}`.replace(
                          /([^:])\/\//g,
                          "$1/"
                        ) // ✅ Fix: Removed semicolon inside template string
                      }
                      alt={`VenueImage ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "150px",
                        objectFit: "cover",
                        cursor: "pointer",
                        borderRadius: "8px",
                      }}
                      onClick={() =>
                        setSelectedImage(
                          `${BASE_URL}${img.path
                            .replace("/home/ubuntu/new/CEP_BackEnd", "")
                            .replace("/home/CEP_BackEnd", "")}`.replace(
                            /([^:])\/\//g,
                            "$1/"
                          ) // Fix double slashes
                        )
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {vendorData.BannerImage && (
            <Grid item xs={12}>
              <Typography variant="h6">Banner Images:</Typography>
              <Grid container spacing={2}>
                {Array.isArray(JSON.parse(vendorData.BannerImage)) &&
                  JSON.parse(vendorData.BannerImage).map((banner, index) => {
                    try {
                      const processedPath = banner.path
                        .replace("/home/CEP_BackEnd", "")
                        .replace("/home/ubuntu/new/CEP_BackEnd", "")
                        .replace(/([^:])\/\//g, "$1/"); // Fix double slashes

                      console.log(processedPath);
                      const imageSrc = `${BASE_URL}${processedPath}`;

                      return (
                        <Grid item xs={3} key={index}>
                          <img
                            src={imageSrc}
                            alt={`Banner Images ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                              cursor: "pointer",
                              borderRadius: "8px",
                            }}
                            onClick={() => setSelectedImage(imageSrc)}
                          />
                        </Grid>
                      );
                    } catch (error) {
                      console.error("Error processing banner image:", error);
                      return null;
                    }
                  })}
              </Grid>
            </Grid>
          )}

          {vendorData.Video && (
            <Grid item xs={12}>
              <Typography variant="h6">Video:</Typography>
              <Grid container spacing={2}>
                {(() => {
                  try {
                    const videoData = JSON.parse(vendorData.Video);
                    if (!videoData || !videoData.path)
                      return <p>Video not available.</p>;

                    // ✅ Remove server path prefixes and fix double slashes
                    const processedVideoPath = videoData.path
                      .replace("/home/CEP_BackEnd", "")
                      .replace("/home/ubuntu/new/CEP_BackEnd", "")
                      .replace(/([^:])\/\//g, "$1/");

                    const videoSrc = `${BASE_URL}${processedVideoPath}`;

                    return videoSrc ? (
                      <div className="video-item">
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: "150px",
                            borderRadius: "8px",
                          }}
                        >
                          <source src={videoSrc} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : (
                      <p style={{ marginTop: "17px", marginLeft: "15px" }}>
                        Video not available.
                      </p>
                    );
                  } catch (error) {
                    console.error("Error processing video:", error);
                    return <p>Invalid video data.</p>;
                  }
                })()}
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>

        {status === "pending" && (
          <>
            <Button
              onClick={handleApprove}
              color="primary"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? "Approving..." : "Approve"}
            </Button>
            <Button
              onClick={handleReject}
              color="error"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? "Rejecting..." : "Reject"}
            </Button>
          </>
        )}

        {status === "approved" && (
          <>
            <Button
              onClick={handleSpecialApprove}
              color="secondary"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? "Updating..." : "Update Special Badges"}
            </Button>
            <Button
              onClick={handleSetPending} // Simulating setting status to pending
              color="warning"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? "Pending..." : "Pending"}
            </Button>
            <Button
              onClick={handleReject}
              color="error"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? "Rejecting..." : "Reject"}
            </Button>
          </>
        )}

        {status === "rejected" && (
          <Button
            onClick={handleSetPending} // Simulating setting status to pending
            color="warning"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? "Pending..." : "Pending"}
          </Button>
        )}
      </DialogActions>

      {/* Image Preview Dialog */}
      {selectedImage && (
        <ImagePopup
          selectedImage={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </Dialog>
  );
};

export default ApproveVendorServiceForm;
